import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { getAllTeams, createTeam, updateTeam, deleteTeam, getTeamCategories } from "@/lib/api";
import { toast } from "sonner";

const initialFormData = {
  name: "",
  logo_url: "",
  category_id: "",
  city: "",
  is_active: true,
};

export default function AdminTeams() {
  const [teams, setTeams] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTeams();
    fetchCategories();
  }, []);

  const fetchTeams = async () => {
    try {
      const response = await getAllTeams();
      setTeams(response.data);
    } catch (error) {
      console.error("Error fetching teams:", error);
      toast.error("Kon teams niet laden");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await getTeamCategories();
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const openNewDialog = () => {
    setFormData(initialFormData);
    setEditingId(null);
    setDialogOpen(true);
  };

  const handleEdit = (team) => {
    setFormData({
      name: team.name,
      logo_url: team.logo_url || "",
      category_id: team.category_id || "",
      city: team.city || "",
      is_active: team.is_active,
    });
    setEditingId(team.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!confirm("Weet je zeker dat je dit team wilt verwijderen?")) return;
    try {
      await deleteTeam(id);
      toast.success("Team succesvol verwijderd");
      fetchTeams();
    } catch (error) {
      console.error("Error deleting team:", error);
      toast.error("Kon team niet verwijderen");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await updateTeam(editingId, formData);
        toast.success("Team succesvol bijgewerkt");
      } else {
        await createTeam(formData);
        toast.success("Team succesvol toegevoegd");
      }
      setDialogOpen(false);
      fetchTeams();
    } catch (error) {
      console.error("Error saving team:", error);
      toast.error("Kon team niet opslaan");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (checked) => {
    setFormData((prev) => ({ ...prev, is_active: checked }));
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find((c) => c.id === categoryId);
    return category ? category.name : "-";
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Users className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Chivo']">Teams</h1>
            <p className="text-slate-600">Beheer teams voor wedstrijden en ranglijsten</p>
          </div>
        </div>
        <Button onClick={openNewDialog} className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="h-4 w-4 mr-2" />
          Team Toevoegen
        </Button>
      </div>

      <Card className="bg-white border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-600">Laden...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50 border-slate-200">
                  <TableHead className="text-slate-600">Logo</TableHead>
                  <TableHead className="text-slate-600">Naam</TableHead>
                  <TableHead className="text-slate-600">Categorie</TableHead>
                  <TableHead className="text-slate-600">Stad</TableHead>
                  <TableHead className="text-slate-600">Status</TableHead>
                  <TableHead className="text-right text-slate-600">Acties</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {teams.length > 0 ? (
                  teams.map((team) => (
                    <TableRow key={team.id} className="border-slate-200">
                      <TableCell>
                        {team.logo_url ? (
                          <img src={team.logo_url} alt={team.name} className="h-10 w-10 object-contain" />
                        ) : (
                          <div className="h-10 w-10 bg-slate-200 rounded flex items-center justify-center">
                            <Users className="h-5 w-5 text-slate-400" />
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-medium text-slate-900">{team.name}</TableCell>
                      <TableCell className="text-slate-600">{getCategoryName(team.category_id)}</TableCell>
                      <TableCell className="text-slate-600">{team.city || "-"}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs ${team.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}`}>
                          {team.is_active ? "Actief" : "Inactief"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" size="sm" onClick={() => handleEdit(team)} className="border-slate-200">
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleDelete(team.id)} className="border-slate-200 text-red-600 hover:text-red-700">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                      Geen teams gevonden. Voeg je eerste team toe!
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900">{editingId ? "Team Bewerken" : "Team Toevoegen"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-slate-700">Naam *</Label>
              <Input id="name" name="name" value={formData.name} onChange={handleChange} required className="border-slate-300" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="logo_url" className="text-slate-700">Logo URL</Label>
              <Input id="logo_url" name="logo_url" value={formData.logo_url} onChange={handleChange} className="border-slate-300" placeholder="https://..." />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700">Categorie</Label>
              <Select value={formData.category_id || "none"} onValueChange={(v) => handleSelectChange("category_id", v === "none" ? "" : v)}>
                <SelectTrigger className="border-slate-300">
                  <SelectValue placeholder="Selecteer categorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Geen categorie</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="city" className="text-slate-700">Stad</Label>
              <Input id="city" name="city" value={formData.city} onChange={handleChange} className="border-slate-300" />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-slate-700">Actief</Label>
              <Switch checked={formData.is_active} onCheckedChange={handleSwitchChange} />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-300">Annuleren</Button>
              <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">{saving ? "Opslaan..." : "Opslaan"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
